import { Currency } from '../types/currency';
import { ChanceToMatch } from '../types/loanType';

interface PredictionInput {
  amount: number;
  interestRate: number;
  period: number;
  currency: Currency;
}

// 'lowestHigh'    — lowest rate that reaches HIGH/VERY_HIGH (widget default)
// 'highestMedium' — highest rate still rated MEDIUM, i.e. the top of the MEDIUM
//                   band just before it tips into HIGH (used by the calculator)
type RateTarget = 'lowestHigh' | 'highestMedium';

interface CalculateOptimalRateParams {
  amount: string;
  period: number;
  currency: Currency;
  minRate: number;
  maxRate: number;
  availableInstantRate: string | null | undefined;
  computeChanceToMatch: (input: PredictionInput) => ChanceToMatch | null;
  target?: RateTarget;
  // Undercut applied to an available instant ticket rate. The widget defaults
  // to 1pp — you'd never post the same rate on the market as an instantly
  // available ticket. The calculator passes 0: it shows the rate you'd
  // actually get, whether via ticket or market.
  instantRateDiscount?: number;
}

const OPTIMIZATION_STEP = 0.5;

export function calculateOptimalRate({
  amount,
  period,
  currency,
  minRate,
  maxRate,
  availableInstantRate,
  computeChanceToMatch,
  target = 'lowestHigh',
  instantRateDiscount = 1,
}: CalculateOptimalRateParams): number {
  const amountNum = parseFloat(amount);
  const isAmount = !isNaN(amountNum) && amountNum > 0;

  // 'lowestHigh': first rate that reaches HIGH/VERY_HIGH.
  // 'highestMedium': last rate still rated MEDIUM (match probability rises with
  // rate, so MEDIUM is a contiguous band — its top is just before HIGH).
  let selectedRate: number | null = null;

  // Start at least at OPTIMIZATION_STEP since model returns null for rate = 0
  const startRate = Math.max(minRate, OPTIMIZATION_STEP);
  for (let rate = startRate; rate <= maxRate && isAmount; rate += OPTIMIZATION_STEP) {
    const chance = computeChanceToMatch({
      amount: amountNum,
      interestRate: rate,
      period,
      currency,
    });

    // If model not available, skip optimization
    if (chance === null) {
      break;
    }

    if (target === 'highestMedium') {
      if (chance === ChanceToMatch.MEDIUM) {
        selectedRate = rate;
      } else if (selectedRate !== null) {
        // Was MEDIUM, now HIGH+ — we've passed the top of the MEDIUM band.
        break;
      } else if (chance === ChanceToMatch.HIGH || chance === ChanceToMatch.VERY_HIGH) {
        // Already above the MEDIUM band before ever entering it — anchor to
        // this rate instead of falling through to the maxRate fallback.
        selectedRate = rate;
        break;
      }
    } else if (chance === ChanceToMatch.HIGH || chance === ChanceToMatch.VERY_HIGH) {
      selectedRate = rate;
      break;
    }
  }

  // If no rate hits the target band, use fallback or instant rate logic
  if (selectedRate === null) {
    // If instant rate is available, use it (minus the discount)
    if (availableInstantRate && availableInstantRate !== '0') {
      const instantRateNum = parseFloat(availableInstantRate);
      return Math.max(minRate, instantRateNum - instantRateDiscount);
    }
    // Otherwise use max rate
    return maxRate;
  }

  // If instant rate is available, cap at it (minus the discount)
  if (availableInstantRate && availableInstantRate !== '0') {
    const instantRateNum = parseFloat(availableInstantRate);
    const cappedInstant = Math.max(minRate, instantRateNum - instantRateDiscount);
    return Math.min(cappedInstant, selectedRate);
  }

  return selectedRate;
}
